<template>
  <div class="project-access">
    <div class="header-actions">
      <h2 class="component-title" id="access-management-title"></h2>
      <div class="action-buttons">
        <button 
          class="btn btn-primary" 
          @click="openInviteDialog"
          aria-label="Invite client"
        >
          <span class="btn-icon">+</span>
          {{ $t('projects.inviteClient') }}
        </button>
      </div>
    </div><div v-if="loading && clientsWithAccess.length === 0" class="loading-container" aria-live="polite">
      <div class="loading-spinner" role="status" aria-label="Loading"></div>
      <p>{{ $t('common.loading') }}</p>
    </div>

    <div v-else-if="error" class="error-container" aria-live="assertive">
      <svg viewBox="0 0 24 24" width="48" height="48">
        <path fill="var(--error, #e53935)" d="M11,15H13V17H11V15M11,7H13V13H11V7M12,2C6.47,2 2,6.5 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20Z" />
      </svg>
      <p>{{ error }}</p>
      <button @click="loadClientAccess" class="btn btn-secondary">{{ $t('common.retry') }}</button>
    </div>

    <div v-if="clientsWithAccess.length === 0" class="empty-state">
      <svg viewBox="0 0 24 24" width="60" height="60" aria-hidden="true">
        <path fill="var(--neutral-300)" d="M12,17A2,2 0 0,0 14,15C14,13.89 13.1,13 12,13A2,2 0 0,0 10,15A2,2 0 0,0 12,17M18,8A2,2 0 0,1 20,10V20A2,2 0 0,1 18,22H6A2,2 0 0,1 4,20V10C4,8.89 4.9,8 6,8H7V6A5,5 0 0,1 12,1A5,5 0 0,1 17,6V8H18M12,3A3,3 0 0,0 9,6V8H15V6A3,3 0 0,0 12,3Z" />
      </svg>
      <p>{{ $t('projects.noClientsWithAccess') }}</p>
      <button class="btn btn-primary mt-4" @click="openInviteDialog">
        {{ $t('projects.inviteClient') }}
      </button>
    </div>

    <div v-else class="grid-container">
      <AppDataGrid
        :columnDefs="columnDefs"
        :rowData="clientsWithAccess"
        :actions="actions"
        :defaultColDef="defaultColDef"
        :pageSizeOptions="[5, 10, 25, 50, 100]"
        :pagination="true"
        :pageSize="20"
        :suppressPaginationPanel="false"
        :animateRows="true"
        :showSearch="true"
        :searchPlaceholder="$t('tasks.placeholders.searchTasks')"
        @grid-ready="onGridReady"
        @action-clicked="handleAction"
        gridStyle="width: 100%;"
        domLayout="autoHeight" 
      />
    </div>    <!-- Access Invite/Edit Dialog -->
    <AccessInvite 
      v-model:show="showInviteDialog" 
      :projectId="projectId"
      :isEditMode="isEditMode"
      :accessId="selectedAccessId"
      :accessData="selectedAccessData"
      @invite-sent="handleInviteSent"
      @access-updated="handleAccessUpdated"
    />

    <!-- Confirmation Dialogs -->
    <ConfirmationDialog
      v-model:show="showDeleteConfirmation"
      :title="$t('projects.confirmDelete')"
      :message="deleteConfirmMessage"
      :confirm-button-text="$t('projects.deleteAccess')"
      confirm-button-type="danger"
      :icon="deleteIcon"
      icon-type="danger"
      :is-loading="isActionLoading"
      @confirm="confirmDeleteAccess"
      @cancel="showDeleteConfirmation = false"
    />

    <ConfirmationDialog
      v-model:show="showResendConfirmation"
      :title="$t('projects.confirmResend')"
      :message="resendConfirmMessage"
      :confirm-button-text="$t('projects.resendInvitation')"
      confirm-button-type="info"
      :icon="resendIcon"
      icon-type="info"
      :is-loading="isActionLoading"
      @confirm="confirmResendInvitation"
      @cancel="showResendConfirmation = false"
    />
  </div>
</template>

<script setup lang="ts">
/// <reference types="vue" />
// @ts-ignore
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import ProjectAccessService from '@/services/projectAccess.service'
import AccessInvite from './AccessInvite.vue'
import ConfirmationDialog from './ConfirmationDialog.vue'
import AppDataGrid from '@/components/base/AppDataGrid.vue'

// Define interfaces
interface ClientAccess {
  id: number | string
  clientName: string
  email: string
  invitationDate: string
  status: string | { name: string }
  [key: string]: any
}

interface ColumnDef {
  field: string
  headerName: string
  sortable?: boolean
  minWidth?: number
  flex?: number
  type?: string
  valueFormatter?: (params: any) => string
  cellClass?: (params: any) => string
}

interface Action {
  type: string
  icon: string
  label: string
  tooltip: string
  class: string
  condition?: (data: ClientAccess) => boolean
}

interface Props {
  projectId: string | number
}

interface Emits {
  refresh: []
}

// Props
const props = defineProps<Props>()

// Emits
const emit = defineEmits<Emits>()

// Composables
const { t } = useI18n()

// Reactive data
const loading = ref(false)
const error = ref<string | null>(null)
const clientsWithAccess = ref<ClientAccess[]>([])
const showInviteDialog = ref(false)
const isEditMode = ref(false)
const gridApi = ref<any>(null)

// Reactive states for edit and confirmation functionality
const showDeleteConfirmation = ref(false)
const showResendConfirmation = ref(false)
const selectedAccessId = ref<number | string | null>(null)
const selectedAccessData = ref<Partial<ClientAccess>>({})
const isActionLoading = ref(false)

// Material Design icons for confirmation dialogs
const deleteIcon = 'M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M8.46,11.88L9.87,10.47L12,12.59L14.12,10.47L15.53,11.88L13.41,14L15.53,16.12L14.12,17.53L12,15.41L9.88,17.53L8.47,16.12L10.59,14L8.46,11.88M15.5,4L14.5,3H9.5L8.5,4H5V6H19V4H15.5Z'
const resendIcon = 'M2,21L23,12L2,3V10L17,12L2,14V21Z'

// Computed properties for confirmation messages
const deleteConfirmMessage = computed(() => {
  return t('projects.deleteConfirmMessage', {
    name: selectedAccessData.value.clientName || ''
  })
})

const resendConfirmMessage = computed(() => {
  return t('projects.resendConfirmMessage', {
    name: selectedAccessData.value.clientName || '',
    email: selectedAccessData.value.email || ''
  })
})

// Column definitions for AppDataGrid - make reactive to language changes
const columnDefs = computed<ColumnDef[]>(() => [
  {
    field: 'clientName',
    headerName: t('projects.clientName'),
    sortable: true,
    minWidth: 180,
    flex: 2
  },
  {
    field: 'email',
    headerName: t('projects.emailAddress'),
    sortable: true,
    minWidth: 220,
    flex: 2
  },
  {
    field: 'invitationDate',
    headerName: t('projects.invitationDate'),
    type: 'date',
    sortable: true,
    minWidth: 150,
    flex: 1
  },
  {
    field: 'status',
    headerName: t('projects.invitationStatus'),
    sortable: true,
    minWidth: 150,
    flex: 1,
    valueFormatter: (params: any) => {
      if (!params.value) return ''

      const status = typeof params.value === 'object' ? params.value : String(params.value)
      const statusValue = typeof status === 'object' ? status.name || status : status

      switch(statusValue) {
        case 'ACCEPTED':
          return t('projects.statusAccepted')
        case 'DECLINED':
          return t('projects.statusDeclined')
        case 'PENDING':
        default:
          return t('projects.statusPending')
      }
    },
    cellClass: (params: any) => {
      if (!params.value) return ''

      const status = typeof params.value === 'object' ? params.value : String(params.value)
      const statusValue = typeof status === 'object' ? status.name || status : status

      switch(statusValue) {
        case 'ACCEPTED':
          return 'status-accepted'
        case 'DECLINED':
          return 'status-declined'
        case 'PENDING':
        default:
          return 'status-pending'
      }
    }
  }
])

// Actions configuration for AppDataGrid - make reactive to language changes
const actions = computed<Action[]>(() => [
  {
    name: 'edit',
    icon: 'fas fa-edit',
    label: t('common.edit'),
    tooltip: t('common.edit'),
    class: 'grid-btn-edit'
  },
  {
    name: 'resend',
    icon: 'fas fa-paper-plane',
    label: t('projects.resendInvitation'),
    tooltip: t('projects.resendInvitation'),
    class: 'grid-btn-resend',
    // Only show for pending invitations
    condition: (data: ClientAccess) => {
      const status = typeof data.status === 'object' ? data.status : String(data.status)
      const statusValue = typeof status === 'object' ? status.name || status : status
      return statusValue === 'PENDING'
    }
  },
  {
    name: 'revoke',
    icon: 'fas fa-trash',
    label: t('projects.revokeAccess'),
    tooltip: t('projects.revokeAccess'),
    class: 'grid-btn-delete'
  }
])

const defaultColDef = reactive({
  flex: 1,
  resizable: true
})

// Function to fetch client access data
const fetchClientAccess = async (): Promise<void> => {
  if (!props.projectId) return

  loading.value = true
  error.value = null

  try {
    const data = await ProjectAccessService.getProjectAccess(String(props.projectId))
    clientsWithAccess.value = data || []
  } catch (err) {
    console.error('Failed to load client access data:', err)
    error.value = t('projects.errorLoadingAccess')
  } finally {
    loading.value = false
  }
}

// Load client access data - this is the main function we'll expose and use
const loadClientAccess = async (): Promise<void> => {
  await fetchClientAccess()
}

// Open invite dialog
const openInviteDialog = (): void => {
  // Reset edit mode state for create mode
  isEditMode.value = false
  selectedAccessId.value = null
  selectedAccessData.value = {}
  showInviteDialog.value = true
}

// Handle successful invitation
const handleInviteSent = (): void => {
  loadClientAccess()
  emit('refresh')
}

// Handle actions from AppDataGrid
const handleAction = (action: any, data: any): void => {
  if (!data || !data.id) return

  switch(action.name) {
    case 'edit':
      handleEditAccess(data.id)
      break
    case 'resend':
      handleResendInvitation(data.id)
      break
    case 'revoke':
      handleRevokeAccess(data.id)
      break
    default:
      console.warn('Unknown action:', action.name)
  }
}

// Handle edit access - opens the edit dialog
const handleEditAccess = (clientId: number | string): void => {
  // Find the client by ID
  const client = clientsWithAccess.value.find((c: ClientAccess) => c.id == clientId)
  if (!client) return

  // Set the selected client data
  selectedAccessId.value = clientId
  selectedAccessData.value = { ...client }

  // Open the dialog in edit mode
  isEditMode.value = true
  showInviteDialog.value = true
}

// Handle access updated event from the edit dialog
const handleAccessUpdated = (): void => {
  // Refresh the client access list
  loadClientAccess()
  emit('refresh')
}

// Handle resend invitation - shows confirmation dialog
const handleResendInvitation = (clientId: number | string): void => {
  // Find the client by ID
  const client = clientsWithAccess.value.find((c: ClientAccess) => c.id == clientId)
  if (!client) return

  // Set the selected client data
  selectedAccessId.value = clientId
  selectedAccessData.value = { ...client }

  // Show confirmation dialog
  showResendConfirmation.value = true
}

// Confirm resend invitation action
const confirmResendInvitation = async (): Promise<void> => {
  if (!selectedAccessId.value) return

  isActionLoading.value = true

  try {
    // Call API service to resend invitation
    await ProjectAccessService.resendInvitation(String(props.projectId), String(selectedAccessId.value))

    // Refresh the client access list
    await loadClientAccess()

    // Show success notification (optional - could implement a toast notification system)
    // For now, we'll just close the dialog
    showResendConfirmation.value = false
  } catch (err) {
    console.error('Failed to resend invitation:', err)
    error.value = t('projects.errorResendingInvitation')
  } finally {
    isActionLoading.value = false
  }
}

// Handle revoke access - shows confirmation dialog
const handleRevokeAccess = (clientId: number | string): void => {
  // Find the client by ID
  const client = clientsWithAccess.value.find((c: ClientAccess) => c.id == clientId)
  if (!client) return

  // Set the selected client data
  selectedAccessId.value = clientId
  selectedAccessData.value = { ...client }

  // Show confirmation dialog
  showDeleteConfirmation.value = true
}

// Confirm delete access action
const confirmDeleteAccess = async (): Promise<void> => {
  if (!selectedAccessId.value) return

  isActionLoading.value = true

  try {
    // Call API service to revoke access
    await ProjectAccessService.revokeAccess(String(props.projectId), String(selectedAccessId.value))

    // Refresh the client access list
    await loadClientAccess()

    // Close the dialog
    showDeleteConfirmation.value = false
  } catch (err) {
    console.error('Failed to revoke access:', err)
    error.value = t('projects.errorRevokingAccess')
  } finally {
    isActionLoading.value = false
  }
}

// Watch for projectId changes to reload data
watch(() => props.projectId, (newVal: string | number) => {
  if (newVal) {
    loadClientAccess()
  }
})

// Set up grid events
const onGridReady = (params: any): void => {
  gridApi.value = params.api
}

// Load data when component mounts
onMounted(() => {
  if (props.projectId) {
    loadClientAccess()
  }
})
</script>

<style scoped>
.project-access {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.header-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.component-title {
  font-size: 1.5rem;
  color: var(--text-primary);
  margin: 0;
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.btn-icon-only {
  width: 40px;
  height: 40px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-text {
  background: transparent;
  border: none;
  color: var(--primary);
  text-decoration: underline;
  cursor: pointer;
}

.btn-text:hover {
  color: var(--primary-dark);
}

.loading-container, .error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid var(--neutral-200);
  border-left-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
}

.btn-primary {
  background-color: var(--primary);
  color: white;
  border: none;
}

.btn-primary:hover {
  background-color: var(--primary-dark);
}

.btn-secondary {
  background-color: var(--neutral-100);
  color: var(--text-primary);
  border: 1px solid var(--neutral-300);
}

.btn-secondary:hover {
  background-color: var(--neutral-200);
}

.btn-icon {
  margin-right: 0.5rem;
  font-weight: bold;
  font-size: 1.1rem;
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-accepted {
  background-color: var(--success-light, #e6f4ea);
  color: var(--success, #34a853);
}

.status-pending {
  background-color: var(--warning-light, #fff8e1);
  color: var(--warning, #fbbc04);
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.empty-state svg {
  margin-bottom: 1.5rem;
}

.empty-state p {
  color: var(--text-secondary);
}

/* Ensure AG Grid fills available space */
.ag-grid-container {
  flex: 1;
  width: 100%;
  min-height: 400px;
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;
}

:deep(.ag-theme-alpine) {
  --ag-font-family: inherit;
  --ag-header-foreground-color: var(--text-primary);
  --ag-background-color: white;
  --ag-selected-row-background-color: var(--primary-lightest, #f2f8ff);
  --ag-row-hover-color: var(--neutral-100, #f8f9fa);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

:deep(.ag-header-cell-text) {
  font-weight: 600;
}

:deep(.ag-row) {
  border-bottom-color: var(--neutral-100, #f8f9fa);
}

:deep(.grid-actions-cell) {
  padding: 0;
}

:deep(.grid-actions) {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.25rem;
  height: 100%;
  padding: 0 0.5rem;
}

:deep(.grid-btn) {
  width: 28px;
  height: 28px;
  border: none;
  background-color: transparent;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  transition: all 0.2s ease;
  padding: 0;
}

:deep(.grid-btn:hover) {
  background-color: var(--neutral-100);
  color: var(--text-primary);
}

:deep(.grid-btn:focus) {
  outline: none;
  box-shadow: 0 0 0 2px rgba(var(--primary-rgb, 0, 123, 255), 0.25);
}

:deep(.grid-btn-edit:hover) {
  color: var(--primary);
}

:deep(.grid-btn-resend:hover) {
  color: var(--warning);
}

:deep(.grid-btn-delete:hover) {
  color: var(--error);
}

:deep(.client-name) {
  font-weight: 500;
}

.mt-4 {
  margin-top: 1rem;
}

/* Responsive design */
@media (max-width: 768px) {
  .header-actions {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .action-buttons {
    width: 100%;
    justify-content: flex-end;
  }
  
  
  :deep(.ag-header-cell-text) {
    white-space: normal;
    overflow: visible;
    text-overflow: clip;
    line-height: 1.2;
  }
  
  :deep(.grid-actions) {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .action-buttons {
    width: 100%;
    flex-wrap: wrap;
  }

  .action-buttons :deep(.app-btn),
  .action-buttons button {
    flex: 1;
    min-width: 0;
  }
}
</style>
